import { useMemo, useState } from 'react';
import {
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Select,
  Input,
  RadioGroup,
  Radio,
  Wrap,
  WrapItem,
  InputGroup,
  InputRightElement,
  Spinner,
} from '@chakra-ui/react';

import SelectSearch from 'components/fields/SelectSearch';
import { CYLINDERS, VEHICLE_YEARS } from '../../constants/vehicleOptions';
import {
  useAnswerCodes,
  useValidationSchema,
  useVehicleLookup,
} from '../../hooks';
import type { VehicleBasicInfoProps } from './types';

const VehicleBasicInfo = ({
  vehicle,
  index,
  onVehicleChange,
  isVinLoading,
  isViccLoading,
  onVinBlur,
  onViccBlur,
  onModelSelect,
  onManualMakeSelect,
  onManualModelSelect,
  onManualYearSelect,
}: VehicleBasicInfoProps) => {
  const { getOptions, getCode, isLoading } = useAnswerCodes();
  const { isFieldRequired } = useValidationSchema();

  const vehicleEntity = vehicle as unknown as Record<string, unknown>;
  const isRequired = (field: string) =>
    isFieldRequired('vehicle', field, vehicleEntity);
  const fuelTypeOptions = getOptions('fuel_type');
  const bodyStyleOptions = getOptions('vehicle_body_type');
  const ownershipOptions = getOptions('vehicle_ownership');

  const vinLookupResult = vehicle.vin_lookup_result;
  const vinLookupFound = vinLookupResult?.status === 'found';
  const hasMultipleModels = vinLookupResult?.status === 'multiple_results';
  const hasViccData = !!vehicle.vicc_available_years?.length;
  const hasViccPopulatedData =
    !!vehicle.vicc_code && !!vehicle.make?.trim() && !!vehicle.model?.trim();
  const isManualEntryMode =
    !vinLookupFound &&
    !hasMultipleModels &&
    !hasViccData &&
    !hasViccPopulatedData;

  // Track if make field has been interacted with (for lazy loading)
  const [makeFocused, setMakeFocused] = useState(false);

  // Fetch manufacturers, models (when make selected), and years (when model selected)
  // Fetch when: make field is focused OR vehicle already has saved manufacturer_ref
  const {
    manufacturers,
    models,
    years,
    isLoadingManufacturers,
    isLoadingModels,
    isLoadingYears,
  } = useVehicleLookup({
    enabled: isManualEntryMode && (makeFocused || !!vehicle.manufacturer_ref),
    manufacturerRef: isManualEntryMode ? vehicle.manufacturer_ref : undefined,
    modelRef: isManualEntryMode ? vehicle.model_ref : undefined,
  });

  // Transform manufacturers/models to SelectSearch options format
  const manufacturerOptions = useMemo(
    () =>
      manufacturers.map((m) => ({
        value: m.ref,
        label: m.description,
      })),
    [manufacturers],
  );

  const modelOptions = useMemo(
    () =>
      models.map((m) => ({
        value: m.ref,
        label: m.description,
      })),
    [models],
  );

  // Transform years to Select options - deduplicated by year
  const manualYearOptions = useMemo(
    () =>
      years.map((y) => ({
        value: y.year,
        label: y.year,
        vehicle_ref: y.vehicle_ref,
        fuel_type: y.fuel_type,
      })),
    [years],
  );

  const isYearLocked =
    (vinLookupFound || hasMultipleModels) && !!vinLookupResult?.year;
  const isMakeLocked =
    (vinLookupFound || hasMultipleModels) && !!vinLookupResult?.make;
  const isModelLocked = vinLookupFound && !!vinLookupResult?.model;
  const isFuelTypeLocked =
    (vinLookupFound && !!vinLookupResult?.fuel_type) ||
    (hasMultipleModels && !!vehicle.vicc_code) ||
    (isManualEntryMode && !!vehicle.vehicle_ref);
  const isViccLocked =
    (vinLookupFound && !!vinLookupResult?.vicc_code) ||
    (hasMultipleModels && !!vehicle.vicc_code);

  const yearOptions = vehicle.vicc_available_years?.length
    ? VEHICLE_YEARS.filter((opt) =>
        vehicle.vicc_available_years!.includes(String(opt.value)),
      )
    : VEHICLE_YEARS;

  const handleManualMakeChange = (value: string | number) => {
    const manufacturerRef = String(value);
    if (!manufacturerRef) return;
    const manufacturer = manufacturers.find((m) => m.ref === manufacturerRef);
    if (manufacturer) {
      onManualMakeSelect(manufacturerRef, manufacturer.description);
    }
  };

  const handleManualModelChange = (value: string | number) => {
    const modelRef = String(value);
    if (!modelRef) return;
    const model = models.find((m) => m.ref === modelRef);
    if (model) {
      onManualModelSelect(modelRef, model.description);
    }
  };

  const handleManualYearChange = (yearValue: string) => {
    if (!yearValue) return;
    const yearData = years.find((y) => y.year === yearValue);
    if (yearData) {
      onManualYearSelect(
        parseInt(yearValue, 10),
        yearData.vehicle_ref,
        yearData.fuel_type,
      );
    }
  };

  return (
    <>
      {/* Row 1: Year, Make, Model, Fuel Type */}
      <Grid
        templateColumns={{
          base: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)',
          lg: '90px repeat(3, 1fr)',
        }}
        gap={4}
        mb={4}
      >
        <GridItem>
          <FormControl
            isRequired={isRequired('vehicle_model_year')}
            isInvalid={
              isRequired('vehicle_model_year') && !vehicle.vehicle_model_year
            }
          >
            <FormLabel fontSize="sm">Year</FormLabel>
            {isManualEntryMode ? (
              <Select
                variant="main"
                size="sm"
                value={vehicle.vehicle_model_year || ''}
                onChange={(e) => handleManualYearChange(e.target.value)}
                isDisabled={
                  !vehicle.model_ref || isVinLoading || isLoadingYears
                }
                placeholder={
                  isLoadingYears
                    ? 'Loading...'
                    : !vehicle.model_ref
                      ? 'Select model first'
                      : 'Select year'
                }
              >
                {manualYearOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            ) : (
              <Select
                variant="main"
                size="sm"
                value={vehicle.vehicle_model_year || ''}
                onChange={(e) =>
                  onVehicleChange(
                    index,
                    'vehicle_model_year',
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
                isDisabled={isYearLocked || isVinLoading}
                placeholder="Year"
              >
                {yearOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            )}
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl
            isRequired={isRequired('make_and_model')}
            isInvalid={isRequired('make_and_model') && !vehicle.make?.trim()}
          >
            <FormLabel fontSize="sm">Make</FormLabel>
            {isManualEntryMode ? (
              <SelectSearch
                options={manufacturerOptions}
                value={vehicle.manufacturer_ref || ''}
                onChange={handleManualMakeChange}
                onFocus={() => !makeFocused && setMakeFocused(true)}
                placeholder="Search make..."
                isDisabled={isVinLoading}
                isLoading={isLoadingManufacturers}
                isInvalid={
                  isRequired('make_and_model') && !vehicle.make?.trim()
                }
              />
            ) : (
              <Input
                variant="main"
                size="sm"
                value={vehicle.make}
                onChange={(e) => onVehicleChange(index, 'make', e.target.value)}
                isDisabled={isMakeLocked || isVinLoading}
                isInvalid={
                  isRequired('make_and_model') && !vehicle.make?.trim()
                }
                placeholder="Enter make"
              />
            )}
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl
            isRequired={isRequired('make_and_model')}
            isInvalid={isRequired('make_and_model') && !vehicle.model?.trim()}
          >
            <FormLabel fontSize="sm">Model</FormLabel>
            {hasMultipleModels && vinLookupResult?.all_results ? (
              <Select
                variant="main"
                size="sm"
                value={vehicle.vicc_code || ''}
                onChange={(e) => onModelSelect(e.target.value)}
                isDisabled={isVinLoading}
                placeholder="Select model"
                isInvalid={
                  isRequired('make_and_model') && !vehicle.model?.trim()
                }
              >
                {vinLookupResult.all_results.map((v) => (
                  <option key={v.vicc_code} value={v.vicc_code}>
                    {v.model}
                  </option>
                ))}
              </Select>
            ) : isManualEntryMode ? (
              <SelectSearch
                options={modelOptions}
                value={vehicle.model_ref || ''}
                onChange={handleManualModelChange}
                placeholder={
                  isLoadingModels
                    ? 'Loading...'
                    : !vehicle.manufacturer_ref
                      ? 'Select make first'
                      : 'Search model...'
                }
                isDisabled={!vehicle.manufacturer_ref || isVinLoading}
                isLoading={isLoadingModels}
                isInvalid={
                  isRequired('make_and_model') && !vehicle.model?.trim()
                }
              />
            ) : (
              <Input
                variant="main"
                size="sm"
                value={vehicle.model}
                onChange={(e) =>
                  onVehicleChange(index, 'model', e.target.value)
                }
                isDisabled={isModelLocked || isVinLoading}
                isInvalid={
                  isRequired('make_and_model') && !vehicle.model?.trim()
                }
                placeholder="Enter model"
              />
            )}
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl>
            <FormLabel fontSize="sm">Fuel Type</FormLabel>
            <Select
              variant="main"
              size="sm"
              value={getCode(vehicle.fuel_type, 'fuel_type')}
              onChange={(e) =>
                onVehicleChange(index, 'fuel_type', e.target.value)
              }
              isDisabled={isFuelTypeLocked || isVinLoading}
              placeholder="Select"
            >
              {fuelTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormControl>
        </GridItem>
      </Grid>

      {/* Row 2: VICC, VIN, Body Style, Cylinders */}
      <Grid
        templateColumns={{
          base: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)',
          lg: '90px repeat(3, 1fr)',
        }}
        gap={4}
        mb={4}
      >
        <GridItem>
          <FormControl>
            <FormLabel fontSize="sm">VICC</FormLabel>
            <InputGroup size="sm">
              <Input
                variant="main"
                value={vehicle.vicc_code}
                onChange={(e) =>
                  onVehicleChange(index, 'vicc_code', e.target.value)
                }
                onBlur={() => onViccBlur(vehicle.vicc_code)}
                maxLength={6}
                isDisabled={isViccLocked || isVinLoading || isViccLoading}
                placeholder={isViccLoading ? 'Looking up...' : 'Code'}
                pr={isViccLoading ? '2.5rem' : undefined}
              />
              {isViccLoading && (
                <InputRightElement height="100%">
                  <Spinner size="sm" color="brand.500" />
                </InputRightElement>
              )}
            </InputGroup>
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl>
            <FormLabel fontSize="sm">VIN</FormLabel>
            <InputGroup size="sm">
              <Input
                variant="main"
                value={vehicle.vin}
                onChange={(e) =>
                  onVehicleChange(index, 'vin', e.target.value.toUpperCase())
                }
                onBlur={() => onVinBlur(vehicle.vin)}
                maxLength={17}
                isDisabled={isVinLoading}
                placeholder="17 characters"
                pr={isVinLoading ? '2.5rem' : undefined}
              />
              {isVinLoading && (
                <InputRightElement height="100%">
                  <Spinner size="sm" color="brand.500" />
                </InputRightElement>
              )}
            </InputGroup>
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl isRequired={isRequired('vehicle_body_type')}>
            <FormLabel fontSize="sm">Body Style</FormLabel>
            <SelectSearch
              options={bodyStyleOptions}
              value={getCode(vehicle.vehicle_body_type, 'vehicle_body_type')}
              onChange={(value) =>
                onVehicleChange(index, 'vehicle_body_type', value ?? '')
              }
              placeholder="Search body style..."
              isDisabled={isVinLoading}
              isLoading={isLoading}
              isInvalid={
                isRequired('vehicle_body_type') && !vehicle.vehicle_body_type
              }
            />
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl>
            <FormLabel fontSize="sm">Cylinders</FormLabel>
            <Select
              variant="main"
              size="sm"
              value={vehicle.cylinders}
              onChange={(e) =>
                onVehicleChange(index, 'cylinders', e.target.value)
              }
              isDisabled={isVinLoading}
              placeholder="Select"
            >
              {CYLINDERS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormControl>
        </GridItem>
      </Grid>

      <FormControl
        isRequired={isRequired('vehicle_ownership')}
        isInvalid={
          isRequired('vehicle_ownership') && !vehicle.vehicle_ownership
        }
      >
        <FormLabel fontSize="sm">Ownership</FormLabel>
        <RadioGroup
          value={getCode(vehicle.vehicle_ownership, 'vehicle_ownership')}
          onChange={(value) =>
            onVehicleChange(index, 'vehicle_ownership', value)
          }
          isDisabled={false}
        >
          <Wrap spacing={4}>
            {ownershipOptions
              .filter((opt) => opt.value)
              .map((opt) => (
                <WrapItem key={opt.value}>
                  <Radio value={opt.value} isDisabled={false}>
                    {opt.label}
                  </Radio>
                </WrapItem>
              ))}
          </Wrap>
        </RadioGroup>
      </FormControl>
    </>
  );
};

export default VehicleBasicInfo;
